import { useCallback, useEffect, useState } from 'react'
import { api, ApiError } from '../lib/api'
import type {
  GiftPurchase,
  GiftPurchaseResponse,
  GiftPurchasesResponse,
  GiftVoteChoice,
} from '../lib/types'

// Contract error codes (build-contract §3.7) → human copy. Unknown codes fall
// back to the server's own message.
const PURCHASE_ERROR_COPY: Record<string, string> = {
  duplicate_proposal: 'There’s already an open proposal for this game.',
  not_giftable: 'This game isn’t giftable for the group any more.',
  price_unavailable: 'Steam has no price for this right now — try again later.',
  recipient_cannot_vote: 'The recipient can’t vote on their own gift.',
  voting_closed: 'Voting on this gift has closed.',
  not_owner: 'Only the proposer or the group owner can do that.',
  feature_disabled: 'Group gifting isn’t enabled right now.',
}

function purchaseErrorMessage(e: unknown): string {
  if (e instanceof ApiError && e.code && PURCHASE_ERROR_COPY[e.code]) {
    return PURCHASE_ERROR_COPY[e.code]
  }
  return e instanceof Error ? e.message : 'Something went wrong — try again.'
}

/**
 * A group's gift purchases (§3.7; server returns open-first) with the three
 * lifecycle mutations. Every mutation replaces the returned `gift_purchase` in
 * state, then calls `onMutated` — voting can fund (the pool → recipient-wallet
 * transfer happens server-side, atomically), and proposing/canceling flips a
 * giftable's open proposal — so the screen passes ONE callback that re-syncs
 * the sibling pool + giftables surfaces and never misses a money move.
 *
 * Throw semantics: `propose` REJECTS on failure (its confirm dialog stays
 * open); `vote`/`cancel` never reject — the card buttons have no rejection
 * consumer, so failures surface via `mutationError` instead.
 * Fetches only while `enabled` (features.group_wallet).
 */
export function useGiftPurchases(
  groupId: string | undefined,
  enabled: boolean,
  onMutated?: () => void,
) {
  const [purchases, setPurchases] = useState<GiftPurchase[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [proposing, setProposing] = useState(false)
  const [mutatingId, setMutatingId] = useState<string | null>(null)
  const [mutationError, setMutationError] = useState<string | null>(null)

  const refresh = useCallback(() => {
    if (!groupId || !enabled) return Promise.resolve()
    return api
      .get<GiftPurchasesResponse>(`/groups/${groupId}/gift_purchases`)
      .then((data) => {
        setPurchases(data.gift_purchases)
        setError(null)
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Could not load gift proposals.')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [groupId, enabled])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const propose = useCallback(
    async (appid: number, recipientUserId: string): Promise<GiftPurchase> => {
      if (!groupId) throw new Error('No group to propose in.')
      setProposing(true)
      setMutationError(null)
      try {
        const data = await api.post<GiftPurchaseResponse>(`/groups/${groupId}/gift_purchases`, {
          appid,
          recipient_user_id: recipientUserId,
        })
        // The endpoint lists open purchases first — a fresh proposal goes on top.
        setPurchases((prev) => [data.gift_purchase, ...prev])
        onMutated?.()
        return data.gift_purchase
      } catch (e) {
        setMutationError(purchaseErrorMessage(e))
        throw e
      } finally {
        setProposing(false)
      }
    },
    [groupId, onMutated],
  )

  const act = useCallback(
    async (id: string, action: 'votes' | 'cancel', body?: unknown) => {
      setMutatingId(id)
      setMutationError(null)
      try {
        const data = await api.post<GiftPurchaseResponse>(`/gift_purchases/${id}/${action}`, body)
        setPurchases((prev) =>
          prev.map((p) => (p.id === data.gift_purchase.id ? data.gift_purchase : p)),
        )
        onMutated?.()
      } catch (e) {
        setMutationError(purchaseErrorMessage(e))
      } finally {
        setMutatingId(null)
      }
    },
    [onMutated],
  )

  const vote = useCallback(
    (id: string, choice: GiftVoteChoice) => act(id, 'votes', { vote: choice }),
    [act],
  )
  const cancel = useCallback((id: string) => act(id, 'cancel'), [act])

  return {
    purchases,
    isLoading: enabled && !!groupId ? isLoading : false,
    error,
    refresh,
    propose,
    vote,
    cancel,
    proposing,
    mutatingId,
    mutationError,
  }
}
